import type { MailReadiness } from '@lms/shared/types';
import { campaignCopy as c } from '../copy/campaign-copy';
import { useLanguage } from '../lib/LanguageContext';

interface Props {
  report?: { status: 'CONNECTED' | 'DISCONNECTED' | 'ERROR'; details?: MailReadiness };
  loading: boolean;
  error?: unknown;
}

export function MailReadinessCard({ report, loading, error }: Props) {
  const { t, language } = useLanguage();
  const details = report?.details;
  const connected = report?.status === 'CONNECTED';
  return (
    <section
      className="min-w-0 rounded-2xl bg-surface-container-low p-6 text-on-surface space-y-4"
      aria-label={t(c.serviceTitle)}
    >
      <h2 className="text-lg font-headline font-bold">{t(c.serviceTitle)}</h2>
      {loading ? (
        <p role="status">{t(c.mailLoading)}</p>
      ) : error || !report ? (
        <p role="alert" className="rounded-xl bg-error-container p-4 text-on-error-container">
          {t(c.mailError)}
        </p>
      ) : (
        <>
          <p className="font-bold">{t(connected ? c.apiConnected : c.apiDisconnected)}</p>
          <p
            className={`rounded-xl p-4 font-semibold ${connected ? 'bg-secondary-container text-on-secondary-container' : 'bg-error-container text-on-error-container'}`}
          >
            {t(connected && details?.canSend === true ? c.ready : c.reviewReadiness)}
          </p>
          <dl className="grid gap-4 text-sm">
            <div>
              <dt className="text-on-surface-variant">{t(c.senderLabel)}</dt>
              <dd className="font-mono font-bold break-all">
                {details?.fromEmail ?? 'no-reply@yts.web.id'}
              </dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">{t(c.endpoint)}</dt>
              <dd className="font-mono break-all">
                {details?.endpoint ?? 'https://api.mailketing.co.id/api/v2/send'}
              </dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">{t(c.authentication)}</dt>
              <dd>{t(c.tokenHidden)}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">{t(c.senderCheck)}</dt>
              <dd>
                {t(
                  details?.senderStatus === 'LISTED'
                    ? c.senderListed
                    : details?.senderStatus === 'NOT_LISTED'
                      ? c.senderNotListed
                      : c.unknown
                )}
              </dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">{t(c.credits)}</dt>
              <dd className="font-bold tabular-nums">
                {details?.credits == null
                  ? t(c.unknown)
                  : details.credits.toLocaleString(language === 'ar' ? 'ar' : 'id-ID')}
              </dd>
            </div>
          </dl>
          {connected && details?.senderStatus !== 'LISTED' && (
            <p className="text-sm leading-relaxed">{t(c.senderHelp)}</p>
          )}
          {connected && (details?.credits == null || details.credits === 0) && (
            <p className="text-sm">{t(c.creditHelp)}</p>
          )}
          {!connected && details?.message && <p className="text-sm">{details.message}</p>}
          <p className="text-sm text-on-surface-variant">{t(c.creditCost)}</p>
          {details?.checkedAt && (
            <p className="text-sm text-on-surface-variant">
              {t(c.updatedAt)}:{' '}
              {new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'id-ID', {
                timeZone: 'Asia/Jakarta',
                dateStyle: 'short',
                timeStyle: 'short',
              }).format(new Date(details.checkedAt))}{' '}
              WIB
            </p>
          )}
        </>
      )}
      <p className="text-sm leading-relaxed text-on-surface-variant">{t(c.mailScope)}</p>
      <a
        href="https://api.mailketing.co.id/docs/"
        target="_blank"
        rel="noreferrer"
        className="inline-flex min-h-12 items-center font-semibold text-primary underline"
      >
        {t(c.mailDocs)}
      </a>
    </section>
  );
}
